/* eslint-disable no-shadow, no-use-before-define */

import resolveConfig from 'tailwindcss/resolveConfig';
import tailwindRawConfig from '@/../tailwind.config';
import colors from '@/../tailwind.colors';
import clientColors from '@/client/tailwind.colors.client';
import tailwindScreens from '@/../tailwind.screens';

export const tailwindConfig = resolveConfig(tailwindRawConfig);

export const tailwindColorsArray = Object.entries(colors).reduce((arr, entry) => {
    const colorValue = entry[1];

    if (typeof colorValue === 'string') return arr.concat(colorValue);

    return arr.concat(Object.values(colorValue));
}, []);

export const tailwindColorsArrayClient = Object.entries(clientColors).reduce((arr, entry) => {
    const colorValue = entry[1];

    if (typeof colorValue === 'string') return arr.concat(colorValue);

    return arr.concat(Object.values(colorValue));
}, []);

export const tailwindSideCharacterMap = {
    t: 'top',
    b: 'bottom',
    l: 'left',
    r: 'right',
    x: 'horizontal',
    y: 'vertical',
    tl: 'top-left',
    tr: 'top-right',
    bl: 'bottom-left',
    br: 'bottom-right',
};

export const tailwindUnitCharacterMap = {
    auto: 'Auto',
    deg: 'Degrees',
    px: 'Pixels',
    rem: 'Pixels',
    '%': 'Percentage',
    vh: 'Screen',
    vw: 'Screen',
    'min-content': 'Min Content',
    'max-content': 'Max Content',
};

export const tailwindPrefix = tailwindConfig.prefix;
export const tailwindSideCharacters = Object.keys(tailwindSideCharacterMap);
export const tailwindScreenNames = Object.keys(tailwindScreens);
export const tailwindScreenValues = Object.values(tailwindConfig.theme.screens);

// leaving out "active" and "focus" until they are needed, in order to keep the file size down
export const tailwindStates = ['hover'];
export const customStates = ['selected', 'expanded', 'matching'];

export class FlexClassDefinition {
    constructor(data) {
        this.base = data.base || ''; // the "base" portion(s) of the class (e.g. "m" for "f-mt-10")
        this.key = data.key || ''; // the key (in the Tailwind config) for this class
        this.prefix = data.prefix || tailwindPrefix; // the prefix before the class (e.g. "f-")
        this.screen = data.screen || ''; // the screen (i.e. breakpoint) that this class is being applied to
        this.side = data.side || ''; // the side that this class is being applied to (e.g. if this was a border-top class, this would contain "t" for "top")
        this.state = data.state || ''; // the state this class is being applied to (e.g. "hover")
        this.tailwindProp = data.tailwindProp || ''; // the name of the Tailwind theme property (see tailwind.config.js)
        this.tailwindDefinitionProp = data.tailwindDefinitionProp || ''; // the name of the Tailwind definition property (see "tailwindDefinitions")
        this.unit = data.unit || ''; // the CSS unit being used by this class (e.g. "rem")
        this.value = data.value || ''; // the value (in the Tailwind config) for this class
    }
}

export class FlexClassGroup {
    constructor(data = {}) {
        this.all = {};
        this.all.default = data.all ? data.all.default : '';

        tailwindStates.forEach((state) => {
            this.all[state] = data.all ? data.all[state] : '';
        });

        customStates.forEach((state) => {
            this.all[state] = data.all ? data.all[state] : '';
        });

        tailwindSideCharacters.forEach((side) => {
            this.all[side] = data.all ? data.all[side] : '';
        });

        tailwindScreenNames.forEach((screen) => {
            this[screen] = {};
            this[screen].default = data[screen] ? data[screen].default : '';

            tailwindStates.forEach((state) => {
                this[screen][state] = data[screen] ? data[screen][state] : '';
            });

            customStates.forEach((state) => {
                this[screen][state] = data[screen] ? data[screen][state] : '';
            });

            tailwindSideCharacters.forEach((side) => {
                this[screen][side] = data[screen] ? data[screen][side] : '';
            });
        });
    }
}

// * if the (optional) "limitTo" argument is used then it will limit the Flex Class Collection
// * ... to only including the Flex Class Group names listed in the array
export class FlexClassCollection {
    constructor(data, limitTo = []) {
        data = data || {};

        Object.keys(tailwindDefinitions).forEach((prop) => {
            if (limitTo.length && !limitTo.includes(prop)) return;

            this[prop] = data[prop] || new FlexClassGroup(data[prop]);
        });
    }
}

export class TailwindDefinition {
    constructor(data) {
        this.base = data.base || ''; // the "base" portion(s) of the class (e.g. "m" for "f-mt-10")
        this.prefix = data.prefix || tailwindPrefix; // the prefix before the class (e.g. "f-")
        this.sides = data.sides || []; // the sides the class can affect (e.g. ['t', 'r', 'b', 'l'] for border widths)
        this.sidePrefix = data.sidePrefix || ''; // the prefix before each side character (e.g. "-" for "f-border-t-10", and "" for "f-mt-10")
        this.screen = data.screen || ''; // the screen size the class affects
        this.state = data.state || ''; // the state the class is being applied to (e.g. "hover")
        this.tailwindProp = data.tailwindProp || ''; // the name of the Tailwind theme property (e.g. "margin", "padding", etc)
        this.units = data.units || []; // the css units the class uses (e.g. "rem")
    }
}

// the majority of the Tailwind definitions map 1-to-1 to the Tailwind theme properties in tailwind.config.js
// the exception is "insert", which has multiple definitions attached
export const tailwindDefinitions = {
    // sample class: f-items-center
    alignItems: new TailwindDefinition({
        base: 'items',
        tailwindProp: 'alignItems',
    }),

    // sample class: f-self-center
    alignSelf: new TailwindDefinition({
        base: 'self',
        tailwindProp: 'alignSelf',
    }),

    // sample class: fu-aspect-ratio-16/9
    aspectRatio: new TailwindDefinition({
        base: 'aspect-ratio',
        prefix: 'fu-',
        tailwindProp: 'aspectRatio',
    }),

    // sample class: fu-aspect-ratio-video-16/9
    aspectRatioVideo: new TailwindDefinition({
        base: 'aspect-ratio-video',
        prefix: 'fu-',
        tailwindProp: 'aspectRatioVideo',
    }),

    // sample class: f-bg-fixed
    backgroundAttachment: new TailwindDefinition({
        base: 'bg',
        tailwindProp: 'backgroundAttachment',
    }),

    // sample class: f-bg-blue-500
    backgroundColor: new TailwindDefinition({
        base: 'bg',
        tailwindProp: 'backgroundColor',
    }),

    // sample class: fu-bg-hidden
    backgroundDisplay: new TailwindDefinition({
        base: 'bg-hidden',
        prefix: 'fu-',
        tailwindProp: 'backgroundDisplay',
    }),

    // sample class: f-bg-center
    backgroundPosition: new TailwindDefinition({
        base: 'bg',
        tailwindProp: 'backgroundPosition',
    }),

    // sample class: f-bg-no-repeat
    backgroundRepeat: new TailwindDefinition({
        base: 'bg',
        tailwindProp: 'backgroundRepeat',
    }),

    // sample class: f-bg-cover
    backgroundSize: new TailwindDefinition({
        base: 'bg',
        tailwindProp: 'backgroundSize',
    }),

    // sample class: fu-blur-100
    blur: new TailwindDefinition({
        base: 'blur',
        prefix: 'fu-',
        tailwindProp: 'blur',
        units: ['px'],
    }),

    // sample class: f-border-blue-500
    borderColor: new TailwindDefinition({
        base: 'border',
        tailwindProp: 'borderColor',
    }),

    // sample class: f-rounded-tl-1
    borderRadius: new TailwindDefinition({
        base: 'rounded',
        sides: ['t', 'l', 'r', 'b'],
        sidePrefix: '-',
        tailwindProp: 'borderRadius',
        units: ['rem', '%'],
    }),

    // sample class: f-border-solid
    borderStyle: new TailwindDefinition({
        base: 'border',
        tailwindProp: 'borderStyle',
    }),

    // sample class: f-border-t-10
    borderWidth: new TailwindDefinition({
        base: 'border',
        sides: ['t', 'r', 'b', 'l'],
        sidePrefix: '-',
        tailwindProp: 'borderWidth',
        units: ['px'],
    }),

    // sample class: f-bottom-0
    bottom: new TailwindDefinition({
        base: 'bottom',
        tailwindProp: 'inset',
        units: ['%'],
    }),

    // sample class: f-shadow-lg
    boxShadow: new TailwindDefinition({
        base: 'shadow',
        tailwindProp: 'boxShadow',
    }),

    // sample class: fu-boxed
    boxed: new TailwindDefinition({
        base: 'boxed',
        prefix: 'fu-',
        tailwindProp: 'boxed',
    }),

    // sample class: fu-contrast-200
    contrast: new TailwindDefinition({
        base: 'contrast',
        prefix: 'fu-',
        tailwindProp: 'contrast',
        units: ['%'],
    }),

    // sample class: f-cursor-pointer
    cursor: new TailwindDefinition({
        base: 'cursor',
        tailwindProp: 'cursor',
    }),

    // sample class: f-block
    display: new TailwindDefinition({
        base: '',
        tailwindProp: 'display',
    }),

    // sample class: fu-ellipses
    ellipses: new TailwindDefinition({
        base: 'ellipses',
        prefix: 'fu-',
        tailwindProp: 'ellipses',
    }),

    // sample class: f-flex-1
    flex: new TailwindDefinition({
        base: 'flex',
        tailwindProp: 'flex',
    }),

    // sample class: f-flex-row
    flexDirection: new TailwindDefinition({
        base: 'flex',
        tailwindProp: 'flexDirection',
    }),

    // sample class: f-flex-wrap
    flexWrap: new TailwindDefinition({
        base: 'flex',
        tailwindProp: 'flexWrap',
    }),

    // sample class: f-float-left
    float: new TailwindDefinition({
        base: 'float',
        tailwindProp: 'float',
    }),

    // sample class: f-font-heading
    fontFamily: new TailwindDefinition({
        base: 'font',
        tailwindProp: 'fontFamily',
    }),

    // sample class: f-text-2
    fontSize: new TailwindDefinition({
        base: 'text',
        tailwindProp: 'fontSize',
        units: ['rem'],
    }),

    // sample class: f-italic
    fontStyle: new TailwindDefinition({
        base: '',
        tailwindProp: 'fontStyle',
    }),

    // sample class: f-font-700
    fontWeight: new TailwindDefinition({
        base: 'font',
        tailwindProp: 'fontWeight',
    }),

    // sample class: fu-grayscale-100
    grayscale: new TailwindDefinition({
        base: 'grayscale',
        prefix: 'fu-',
        tailwindProp: 'grayscale',
        units: ['%'],
    }),

    // sample class: f-h-4 (rem)
    // sample class: f-h-1/1 (100%)
    // sample class: f-h-screen (100vh)
    height: new TailwindDefinition({
        base: 'h',
        tailwindProp: 'height',
        units: ['auto', '%', 'rem'],
    }),

    // sample class: f-inset-x-0
    inset: new TailwindDefinition({
        base: 'inset',
        sides: ['x', 'y'],
        sidePrefix: '-',
        tailwindProp: 'inset',
        units: ['%'],
    }),

    // sample class: f-justify-center
    justifyContent: new TailwindDefinition({
        base: 'justify',
        tailwindProp: 'justifyContent',
    }),

    // sample class: f-left-0
    left: new TailwindDefinition({
        base: 'left',
        tailwindProp: 'inset',
        units: ['%'],
    }),

    // sample class: f-tracking-wide
    letterSpacing: new TailwindDefinition({
        base: 'tracking',
        tailwindProp: 'letterSpacing',
        units: ['em'],
    }),

    // sample class: f-leading-tight
    lineHeight: new TailwindDefinition({
        base: 'leading',
        tailwindProp: 'lineHeight',
    }),

    // sample class: f-list-disc
    listStyleType: new TailwindDefinition({
        base: 'list',
        tailwindProp: 'listStyleType',
    }),

    // sample class: f-mt-4
    margin: new TailwindDefinition({
        base: 'm',
        sides: ['t', 'r', 'b', 'l', 'x', 'y'],
        tailwindProp: 'margin',
        units: ['%', 'rem'],
    }),

    // sample class: f-max-h-1/1
    maxHeight: new TailwindDefinition({
        base: 'max-h',
        tailwindProp: 'maxHeight',
        units: ['%', 'rem', 'vh'],
    }),

    // sample class: f-max-w-1/1
    maxWidth: new TailwindDefinition({
        base: 'max-w',
        tailwindProp: 'maxWidth',
        units: ['%', 'rem', 'vw'],
    }),

    // sample class: f-min-h-1/1
    minHeight: new TailwindDefinition({
        base: 'min-h',
        tailwindProp: 'minHeight',
        units: ['%', 'rem', 'vh'],
    }),

    // sample class: f-min-w-1/1
    minWidth: new TailwindDefinition({
        base: 'min-w',
        tailwindProp: 'minWidth',
        units: ['%', 'rem', 'vw'],
    }),

    // sample class: f-object-cover
    objectFit: new TailwindDefinition({
        base: 'object',
        tailwindProp: 'objectFit',
    }),

    // sample class: f-object-position
    objectPosition: new TailwindDefinition({
        base: 'object',
        tailwindProp: 'objectPosition',
    }),

    // sample class: f-opacity-50
    opacity: new TailwindDefinition({
        base: 'opacity',
        tailwindProp: 'opacity',
    }),

    // sample class: f-overflow-hidden
    overflow: new TailwindDefinition({
        base: 'overflow',
        sides: ['x', 'y'],
        tailwindProp: 'overflow',
        sidePrefix: '-',
    }),

    // sample class: f-pt-4
    padding: new TailwindDefinition({
        base: 'p',
        sides: ['t', 'r', 'b', 'l', 'x', 'y'],
        tailwindProp: 'padding',
        units: ['%', 'rem'],
    }),

    // sample class: fu-placement-top
    placement: new TailwindDefinition({
        base: 'placement',
        prefix: 'fu-',
        tailwindProp: 'placement',
    }),

    // sample class: f-absolute
    position: new TailwindDefinition({
        base: '',
        tailwindProp: 'position',
    }),

    // sample class: f-right-0
    right: new TailwindDefinition({
        base: 'right',
        tailwindProp: 'inset',
        units: ['%'],
    }),

    // sample class: fu-skew-10
    skew: new TailwindDefinition({
        base: 'skew',
        prefix: 'fu-',
        tailwindProp: 'skew',
        units: ['deg'],
    }),

    // sample class: fu-sticky
    sticky: new TailwindDefinition({
        base: 'sticky',
        prefix: 'fu-',
        tailwindProp: 'sticky',
    }),

    // sample class: fu-tab-dock-location-left
    tabDockLocation: new TailwindDefinition({
        base: 'tab-dock-location',
        prefix: 'fu-',
        tailwindProp: 'tabDockLocation',
    }),

    // sample class: f-text-center
    textAlign: new TailwindDefinition({
        base: 'text',
        tailwindProp: 'textAlign',
    }),

    // sample class: f-text-blue-500
    textColor: new TailwindDefinition({
        base: 'text',
        tailwindProp: 'textColor',
    }),

    // sample class: f-no-underline
    textDecoration: new TailwindDefinition({
        base: '',
        tailwindProp: 'textDecoration',
    }),

    // sample class: f-uppercase
    textTransform: new TailwindDefinition({
        base: '',
        tailwindProp: 'textTransform',
    }),

    // sample class: f-top-0
    top: new TailwindDefinition({
        base: 'top',
        tailwindProp: 'inset',
        units: ['%'],
    }),

    // sample class: fu-origin-left-top
    transformOrigin: new TailwindDefinition({
        base: 'origin',
        prefix: 'fu-',
        tailwindProp: 'transformOrigin',
    }),

    // sample class: fu-transition-1.5
    transition: new TailwindDefinition({
        base: 'transition',
        prefix: 'fu-',
        tailwindProp: 'transition',
    }),

    // sample class: f-align-top
    verticalAlign: new TailwindDefinition({
        base: 'align',
        tailwindProp: 'verticalAlign',
    }),

    // sample class: f-invisible
    visibility: new TailwindDefinition({
        base: '',
        tailwindProp: 'visibility',
    }),

    // sample class: f-whitespace-no-wrap
    whitespace: new TailwindDefinition({
        base: 'whitespace',
        tailwindProp: 'whitespace',
    }),

    // sample class: f-w-4 (rem)
    // sample class: f-w-1/2 (%)
    width: new TailwindDefinition({
        base: 'w',
        tailwindProp: 'width',
        units: ['auto', '%', 'rem', 'vw', 'min-content', 'max-content'],
    }),

    // sample class: f-break-words
    wordBreak: new TailwindDefinition({
        base: 'break',
        tailwindProp: 'wordBreak',
    }),

    // sample class: f-z-1
    zIndex: new TailwindDefinition({
        base: 'z',
        tailwindProp: 'zIndex',
    }),

    // sample class: fu-zoom-1.5
    zoom: new TailwindDefinition({
        base: 'zoom',
        prefix: 'fu-',
        tailwindProp: 'zoom',
    }),
};

// the class dictionary object lists and defines all of the possible Tailwind classes
// each key represents the class name, and the value is the definition
export const classDictionary = {};

// @TODO - add better documentation for the following
Object.entries(tailwindDefinitions).forEach((entry) => {
    const [typeDefinitionName, typeDefinition] = entry;

    Object.entries(tailwindConfig.theme[typeDefinition.tailwindProp]).forEach((entry) => {
        const [configPropName, configPropValue] = entry;
        const unit = typeDefinition.units.find(unitStr => configPropValue.includes(unitStr));

        // most of the colors are represented as objects now
        // so we'll need to loop through them
        if (typeof configPropValue === 'object') {
            Object.entries(configPropValue).forEach((entry) => {
                const [subPropName, subPropValue] = entry;

                // this is the key being added into the class dictionary
                const dictionaryPropName = [
                    typeDefinition.prefix,
                    typeDefinition.base,
                    '-',
                    configPropName,
                    typeDefinition.base && subPropName ? '-' : '',
                    subPropName,
                ].join('');

                // sample class = "f-text-blue-500"
                const dictionaryDefinition = {
                    base: `${ typeDefinition.base }-${ configPropName }`, // e.g. "text-blue"
                    key: subPropName, // e.g. "500"
                    tailwindDefinitionProp: typeDefinitionName, // e.g. "textColor"
                    value: subPropValue, // e.g. "#4299e1"
                    tailwindProp: typeDefinition.tailwindProp, // e.g. "textColor"
                    screen: 'all',
                };

                addDefinition(typeDefinition, dictionaryPropName, dictionaryDefinition);
            });

            return;
        }

        let key = configPropName;
        let base = typeDefinition.base;

        // negative value
        if (configPropName.startsWith('-')) {
            key = key.slice(1);
            base = `-${ base }`;
        }

        const dictionaryPropName = [
            typeDefinition.prefix,
            base,
            typeDefinition.base && configPropName ? '-' : '',
            key,
        ].join('');

        const dictionaryDefinition = new FlexClassDefinition({
            base,
            tailwindDefinitionProp: typeDefinitionName,
            key: configPropName,
            prefix: typeDefinition.prefix,
            value: configPropValue,
            tailwindProp: typeDefinition.tailwindProp,
            unit,
            screen: 'all',
        });

        addDefinition(typeDefinition, dictionaryPropName, dictionaryDefinition);

        typeDefinition.sides.forEach((side) => {
            const dictionaryPropName = [
                typeDefinition.prefix,
                base,
                typeDefinition.sidePrefix,
                side,
                '-',
                key,
            ].join('');

            const sideClassDictionaryDefinition = new FlexClassDefinition({
                ...dictionaryDefinition,
                side,
            });

            addDefinition(typeDefinition, dictionaryPropName, sideClassDictionaryDefinition);
        });
    });
});

function addDefinition(typeDefinition, dictionaryPropName, dictionaryDefinition) {
    classDictionary[dictionaryPropName] = dictionaryDefinition;

    // add in the state variants for each class (e.g. "hover:", "focus:", "active:")
    tailwindStates.forEach((state) => {
        if (tailwindConfig.variants[typeDefinition.tailwindProp].includes(state)) {
            classDictionary[`${ state }:${ dictionaryPropName }`] = new FlexClassDefinition({
                ...dictionaryDefinition,
                state,
            });
        }
    });

    // add in the custom state variants for each class (e.g. "selected:", "expanded:", "matching:")
    customStates.forEach((state) => {
        if (tailwindConfig.variants[typeDefinition.tailwindProp].includes(state)) {
            classDictionary[`${ state }:${ dictionaryPropName }`] = new FlexClassDefinition({
                ...dictionaryDefinition,
                state,
            });
        }
    });

    // add in the responsive variants for each class (e.g. "mob:", "tab:", "lap:", "des:")
    if (tailwindConfig.variants[typeDefinition.tailwindProp].includes('responsive')) {
        tailwindScreenNames.forEach((screenName) => {
            classDictionary[`${ screenName }:${ dictionaryPropName }`] = new FlexClassDefinition({
                ...dictionaryDefinition,
                screen: screenName,
            });

            // add in the state variants for each responsive variant
            tailwindStates.forEach((state) => {
                if (tailwindConfig.variants[typeDefinition.tailwindProp].includes(state)) {
                    classDictionary[`${ screenName }:${ state }:${ dictionaryPropName }`] = new FlexClassDefinition({
                        ...dictionaryDefinition,
                        screen: screenName,
                        state,
                    });
                }
            });

            // add in the custom state variants for each responsive variant
            customStates.forEach((state) => {
                if (tailwindConfig.variants[typeDefinition.tailwindProp].includes(state)) {
                    classDictionary[`${ screenName }:${ state }:${ dictionaryPropName }`] = new FlexClassDefinition({
                        ...dictionaryDefinition,
                        screen: screenName,
                        state,
                    });
                }
            });
        });
    }
}
